import { Box } from "@mui/material";
import { useImageWithRetry } from "../../../hooks/useImageWithRetry";
import { CMChip } from "../../common/CMChip";
import { TileStatusOverlay } from "../../common/TileStatusOverlay";
import { MediaCarouselItem } from "../../common/media-carousel/MediaCarousel";

/**
 * Wrapper component for MediaCarouselItem with smart image retry logic
 * Shows "Generating Thumbnail" overlay for newly uploaded images and retries loading
 */
interface MediaItemWithRetryProps {
  item: any;
  isNewlyUploaded?: boolean;
  onSuccess?: () => void;
}

export const MediaItemWithRetry = ({ item, isNewlyUploaded = false, onSuccess }: MediaItemWithRetryProps) => {
  const { imageKey, isGenerating, isPermanentlyBroken, handleImageError, handleImageLoad, retryCount } = useImageWithRetry({
    maxRetries: 8,
    onSuccess: () => {
      console.log(`[MediaItemWithRetry] Thumbnail loaded for media ${item.id}`);
      onSuccess?.();
    },
    onMaxRetriesReached: () => {
      console.warn(`[MediaItemWithRetry] Maximum retries reached for media ${item.id}`);
    }
  });

  // Only show retry UI for newly uploaded items
  const showRetryUI = isNewlyUploaded;

  return (
    <MediaCarouselItem {...item} key={`${item.id}-${imageKey}`}>
      {/* Show generating overlay for newly uploaded images via shared TileStatusOverlay */}
      {showRetryUI && isGenerating && (
        <TileStatusOverlay variant='loading' label='Generating Thumbnail' subLabel={retryCount > 0 ? `Retry ${retryCount}/8` : undefined} />
      )}

      {/* Show permanently broken state */}
      {showRetryUI && isPermanentlyBroken && <TileStatusOverlay variant='error' label='Thumbnail unavailable' />}

      {/* Image with retry handlers (only for newly uploaded) */}
      {showRetryUI && item.entity_thumbnail && (
        <Box
          component='img'
          src={item.entity_thumbnail}
          alt={item.entity_name || item.name || ""}
          onError={handleImageError}
          onLoad={handleImageLoad}
          sx={{ display: "none" }} // Hidden element just for loading detection
        />
      )}

      {/* Primary badge */}
      {item?.is_primary && (
        <CMChip
          label='Primary'
          sx={{
            position: "absolute",
            height: "1.25rem",
            top: "0.5rem",
            left: "0.5rem",
            bgcolor: "primary.main",
            fontSize: "0.75rem",
            zIndex: 2
          }}
        />
      )}
    </MediaCarouselItem>
  );
};
